/**
 * ConnectorToolbar - Toolbar for customizing connector properties
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { 
  ArrowRight, 
  ArrowLeft, 
  Minus, 
  Type, 
  Palette,
  GripVertical,
  X,
  Circle,
  Route,
  GitBranch
} from 'lucide-react';
import { ConnectorElement, ConnectorArrowStyle, ConnectorPathType } from '../editing/types';

interface ConnectorToolbarProps {
  connectors: ConnectorElement[];
  onUpdate: (updates: Partial<ConnectorElement['data']>) => void;
  position?: { x: number; y: number };
  /** Bounds of the selected element(s) in screen coordinates */
  elementBounds?: { x: number; y: number; width: number; height: number };
  /** Container element for viewport bounds calculation */
  containerRef?: React.RefObject<HTMLDivElement>;
  onClose?: () => void;
}

export const ConnectorToolbar: React.FC<ConnectorToolbarProps> = ({
  connectors,
  onUpdate,
  position,
  elementBounds,
  containerRef,
  onClose,
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  const [showStartArrowMenu, setShowStartArrowMenu] = useState(false);
  const [showEndArrowMenu, setShowEndArrowMenu] = useState(false);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const startArrowRef = useRef<HTMLDivElement>(null);
  const endArrowRef = useRef<HTMLDivElement>(null);
  const [toolbarSize, setToolbarSize] = useState({ width: 0, height: 0 });
  const [isVisible, setIsVisible] = useState(false);
  const [isPositionedAbove, setIsPositionedAbove] = useState(false);
  
  // Measure toolbar size
  useEffect(() => {
    if (toolbarRef.current) {
      const rect = toolbarRef.current.getBoundingClientRect();
      setToolbarSize({ width: rect.width, height: rect.height });
    }
  }, [connectors]);

  // Trigger animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Get representative data (first connector) for display
  const data = connectors[0]?.data;
  if (!data) return null;

  // Check if a property has mixed values across connectors
  const hasMixedValues = (prop: keyof ConnectorElement['data']): boolean => {
    if (connectors.length <= 1) return false;
    const values = connectors.map(c => c.data[prop]);
    return new Set(values).size > 1;
  };

  // Get common value or null if mixed
  const getCommonValue = <T,>(prop: keyof ConnectorElement['data']): T | null => {
    if (hasMixedValues(prop)) return null;
    return data[prop] as T;
  };

  // Extract common values for UI
  const strokeColor = getCommonValue<string>('stroke');
  const strokeWidth = getCommonValue<'small' | 'medium' | 'large' | 'xlarge'>('strokeWidth');
  const dashEnabled = getCommonValue<boolean>('dashEnabled');
  const pathType = getCommonValue<ConnectorPathType>('pathType');
  const arrowStart = getCommonValue<ConnectorArrowStyle>('arrowStart');
  const arrowEnd = getCommonValue<ConnectorArrowStyle>('arrowEnd');
  const text = getCommonValue<string>('text');
  const textBackgroundEnabled = getCommonValue<boolean>('textBackgroundEnabled');

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(target) &&
        toolbarRef.current &&
        !toolbarRef.current.contains(target)
      ) {
        setShowColorPicker(false);
      }
      if (
        startArrowRef.current &&
        !startArrowRef.current.contains(target) &&
        toolbarRef.current &&
        !toolbarRef.current.contains(target)
      ) {
        setShowStartArrowMenu(false);
      }
      if (
        endArrowRef.current &&
        !endArrowRef.current.contains(target) &&
        toolbarRef.current &&
        !toolbarRef.current.contains(target)
      ) {
        setShowEndArrowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate viewport-aware position
  const calculatePosition = () => {
    const TOOLBAR_HEIGHT = 50; // Approximate toolbar height
    const TOOLBAR_MARGIN = 10; // Margin from element
    const VIEWPORT_PADDING = 10; // Padding from viewport edges
    
    // Get container bounds for viewport calculation
    const containerBounds = containerRef?.current?.getBoundingClientRect() || {
      top: 0,
      left: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      width: window.innerWidth,
      height: window.innerHeight,
    };
    
    let x = position?.x || 0;
    let y = position?.y || 0;
    let positionedAbove = false;
    
    if (elementBounds) {
      // Calculate available space above and below the element
      const spaceAbove = elementBounds.y - containerBounds.top;
      const spaceBelow = containerBounds.bottom - (elementBounds.y + elementBounds.height);
      
      // Position horizontally centered on element
      x = elementBounds.x + elementBounds.width / 2;
      
      // Position above or below based on available space
      if (spaceAbove >= TOOLBAR_HEIGHT + TOOLBAR_MARGIN) {
        // Position above element
        y = elementBounds.y - TOOLBAR_MARGIN;
        positionedAbove = true;
      } else if (spaceBelow >= TOOLBAR_HEIGHT + TOOLBAR_MARGIN) {
        // Position below element
        y = elementBounds.y + elementBounds.height + TOOLBAR_HEIGHT + TOOLBAR_MARGIN;
        positionedAbove = false;
      } else {
        // Not enough space above or below, position at top of viewport
        y = containerBounds.top + VIEWPORT_PADDING + TOOLBAR_HEIGHT;
        positionedAbove = true;
      }
    } else if (position) {
      // Fallback: Check if there's enough space above the position
      const spaceAbove = position.y - containerBounds.top;
      if (spaceAbove < TOOLBAR_HEIGHT + TOOLBAR_MARGIN) {
        // Not enough space above, position below
        y = position.y + TOOLBAR_HEIGHT + TOOLBAR_MARGIN * 2;
        positionedAbove = false;
      } else {
        y = position.y - TOOLBAR_MARGIN;
        positionedAbove = true;
      }
    }
    
    // Clamp horizontal position to stay within viewport
    const toolbarWidth = toolbarSize.width || 300; // Estimated width
    const halfWidth = toolbarWidth / 2;
    x = Math.max(containerBounds.left + halfWidth + VIEWPORT_PADDING, x);
    x = Math.min(containerBounds.right - halfWidth - VIEWPORT_PADDING, x);
    
    return { x, y, positionedAbove };
  };
  
  const calculatedPosition = useMemo(() => calculatePosition(), [
    elementBounds?.x,
    elementBounds?.y,
    elementBounds?.width,
    elementBounds?.height,
    position?.x,
    position?.y,
    toolbarSize.width,
  ]);

  // Update position state when position changes
  useEffect(() => {
    setIsPositionedAbove(calculatedPosition.positionedAbove);
  }, [calculatedPosition.positionedAbove]);
  
  // Determine transform based on position and animation state
  const slideOffset = isPositionedAbove ? '-20px' : '20px';
  const translateY = isVisible ? '0' : slideOffset;
  const transformValue = `translate(-50%, -100%) translateY(${translateY})`;
  
  const toolbarStyle: React.CSSProperties = {
    position: 'absolute',
    left: calculatedPosition.x,
    top: calculatedPosition.y,
    transform: transformValue,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 8px',
    backgroundColor: 'white',
    border: '1px solid rgb(229, 231, 235)',
    borderRadius: '9999px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    opacity: isVisible ? 1 : 0,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const buttonStyle = (isActive: boolean): React.CSSProperties => ({
    width: '32px',
    height: '32px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: isActive ? 'rgba(0, 134, 201, 0.1)' : 'transparent',
    color: isActive ? '#0086C9' : 'rgb(107, 114, 128)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
  });

  const dividerStyle: React.CSSProperties = {
    width: '1px',
    height: '24px',
    backgroundColor: 'rgb(229, 231, 235)',
    margin: '0 4px',
  };

  const colorInputStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    border: '1px solid rgb(229, 231, 235)',
    borderRadius: '6px',
    cursor: 'pointer',
    padding: '2px',
  };

  const presetColors = [
    '#000000', '#374151', '#6B7280', '#9CA3AF',
    '#EF4444', '#F59E0B', '#10B981', '#3B82F6',
    '#8B5CF6', '#EC4899', '#F97316', '#14B8A6',
  ];

  const arrowStyles: Array<{ value: ConnectorArrowStyle; icon: React.ReactNode; label: string; isDirectional?: boolean }> = [
    { value: 'none', icon: <Minus size={16} />, label: 'None', isDirectional: false },
    { value: 'solid', icon: <ArrowRight size={16} />, label: 'Solid', isDirectional: true },
    { value: 'line', icon: <ArrowRight size={16} strokeWidth={1.5} />, label: 'Line', isDirectional: true },
    { value: 'triangle', icon: <ArrowRight size={16} />, label: 'Triangle', isDirectional: true },
    { value: 'diamond', icon: <GripVertical size={16} />, label: 'Diamond', isDirectional: false },
    { value: 'circle', icon: <Circle size={14} fill="currentColor" />, label: 'Circle', isDirectional: false },
  ];

  return (
    <div ref={toolbarRef} style={toolbarStyle}>
      {/* Stroke Color */}
      <div style={{ position: 'relative' }}>
        <button
          style={buttonStyle(false)}
          onClick={() => setShowColorPicker(!showColorPicker)}
          title="Stroke Color"
        >
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '4px',
              backgroundColor: data.stroke,
              border: '1px solid rgb(229, 231, 235)',
            }}
          />
        </button>
        {showColorPicker && (
          <div
            ref={colorPickerRef}
            style={{
              position: 'absolute',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              border: '1px solid rgb(229, 231, 235)',
              borderRadius: '8px',
              padding: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '4px',
              zIndex: 1001,
            }}
          >
            {presetColors.map((color) => (
              <button
                key={color}
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '4px',
                  backgroundColor: color,
                  border: '1px solid rgb(229, 231, 235)',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  onUpdate({ stroke: color });
                  setShowColorPicker(false);
                }}
              />
            ))}
            <input
              type="color"
              value={strokeColor || '#000000'}
              onChange={(e) => onUpdate({ stroke: e.target.value })}
              style={colorInputStyle}
              title="Custom Color"
            />
          </div>
        )}
      </div>

      <div style={dividerStyle} />

      {/* Stroke Width - 4 sizes */}
      <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
        {(['small', 'medium', 'large', 'xlarge'] as const).map((size) => {
          const sizeMap = { small: 3, medium: 5, large: 7, xlarge: 9 };
          const isActive = strokeWidth === size && !hasMixedValues('strokeWidth');
          return (
            <button
              key={size}
              style={buttonStyle(isActive)}
              onClick={() => onUpdate({ strokeWidth: size })}
              title={`${size.charAt(0).toUpperCase() + size.slice(1)} (${sizeMap[size]}px)`}
              disabled={hasMixedValues('strokeWidth')}
            >
              <Minus 
                size={16} 
                strokeWidth={sizeMap[size] / 2} 
                opacity={hasMixedValues('strokeWidth') ? 0.5 : 1} 
              />
            </button>
          );
        })}
      </div>

      {/* Dash */}
      <button
        style={buttonStyle(dashEnabled === true && !hasMixedValues('dashEnabled'))}
        onClick={() => onUpdate({ dashEnabled: !dashEnabled })}
        title="Dashed Line"
        disabled={hasMixedValues('dashEnabled')}
      >
        <Minus size={20} strokeDasharray={dashEnabled ? '5,5' : undefined} opacity={hasMixedValues('dashEnabled') ? 0.5 : 1} />
      </button>

      <div style={dividerStyle} />

      {/* Path Type - Straight, Bent, Curved */}
      <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
        <button
          style={buttonStyle(pathType === 'straight' && !hasMixedValues('pathType'))}
          onClick={() => onUpdate({ pathType: 'straight' })}
          title="Straight Connector"
          disabled={hasMixedValues('pathType')}
        >
          <Minus size={16} opacity={hasMixedValues('pathType') ? 0.5 : 1} />
        </button>
        <button
          style={buttonStyle(pathType === 'bent' && !hasMixedValues('pathType'))}
          onClick={() => onUpdate({ pathType: 'bent' })}
          title="Bent Connector"
          disabled={hasMixedValues('pathType')}
        >
          <Route size={16} opacity={hasMixedValues('pathType') ? 0.5 : 1} />
        </button>
        <button
          style={buttonStyle(pathType === 'curved' && !hasMixedValues('pathType'))}
          onClick={() => onUpdate({ pathType: 'curved' })}
          title="Curved Connector"
          disabled={hasMixedValues('pathType')}
        >
          <GitBranch size={16} opacity={hasMixedValues('pathType') ? 0.5 : 1} />
        </button>
      </div>

      <div style={dividerStyle} />

      {/* Start Arrow */}
      <div style={{ position: 'relative' }}>
        <button
          style={buttonStyle(arrowStart !== 'none' && !hasMixedValues('arrowStart'))}
          onClick={() => setShowStartArrowMenu(!showStartArrowMenu)}
          title="Start Arrow Type"
          disabled={hasMixedValues('arrowStart')}
        >
          {(() => {
            const currentStyle = arrowStyles.find(s => s.value === arrowStart);
            if (currentStyle && arrowStart !== 'none') {
              // Flip directional arrows for start (pointing left), keep non-directional as-is
              if (currentStyle.isDirectional) {
                return <span style={{ transform: 'scaleX(-1)', display: 'inline-block' }}>{currentStyle.icon}</span>;
              }
              return currentStyle.icon;
            }
            return <ArrowLeft size={16} opacity={hasMixedValues('arrowStart') ? 0.5 : 1} />;
          })()}
        </button>
        {showStartArrowMenu && (
          <div
            ref={startArrowRef}
            style={{
              position: 'absolute',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              border: '1px solid rgb(229, 231, 235)',
              borderRadius: '8px',
              padding: '4px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              zIndex: 1001,
            }}
          >
            {arrowStyles.map((style) => (
              <button
                key={style.value}
                style={{
                  ...buttonStyle(arrowStart === style.value && !hasMixedValues('arrowStart')),
                  width: '28px',
                  height: '28px',
                }}
                onClick={() => {
                  onUpdate({ arrowStart: style.value });
                  setShowStartArrowMenu(false);
                }}
                title={style.label}
              >
                {style.icon}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* End Arrow */}
      <div style={{ position: 'relative' }}>
        <button
          style={buttonStyle(arrowEnd !== 'none' && !hasMixedValues('arrowEnd'))}
          onClick={() => setShowEndArrowMenu(!showEndArrowMenu)}
          title="End Arrow Type"
          disabled={hasMixedValues('arrowEnd')}
        >
          {(() => {
            const currentStyle = arrowStyles.find(s => s.value === arrowEnd);
            if (currentStyle && arrowEnd !== 'none') {
              return currentStyle.icon;
            }
            return <ArrowRight size={16} opacity={hasMixedValues('arrowEnd') ? 0.5 : 1} />;
          })()}
        </button>
        {showEndArrowMenu && (
          <div
            ref={endArrowRef}
            style={{
              position: 'absolute',
              bottom: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              border: '1px solid rgb(229, 231, 235)',
              borderRadius: '8px',
              padding: '4px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              zIndex: 1001,
            }}
          >
            {arrowStyles.map((style) => (
              <button
                key={style.value}
                style={{
                  ...buttonStyle(arrowEnd === style.value && !hasMixedValues('arrowEnd')),
                  width: '28px',
                  height: '28px',
                }}
                onClick={() => {
                  onUpdate({ arrowEnd: style.value });
                  setShowEndArrowMenu(false);
                }}
                title={style.label}
              >
                {style.icon}
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={dividerStyle} />

      {/* Text Label */}
      <button
        style={buttonStyle(!!text && !hasMixedValues('text'))}
        onClick={() => {
          if (!text) {
            setShowTextInput(true);
          } else {
            onUpdate({ text: undefined });
          }
        }}
        title="Add Text Label"
        disabled={hasMixedValues('text')}
      >
        <Type size={16} opacity={hasMixedValues('text') ? 0.5 : 1} />
      </button>

      {showTextInput && (
        <input
          type="text"
          placeholder="Label text"
          defaultValue={text || ''}
          autoFocus
          onBlur={(e) => {
            if (e.target.value.trim()) {
              onUpdate({ text: e.target.value.trim() });
            }
            setShowTextInput(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (e.currentTarget.value.trim()) {
                onUpdate({ text: e.currentTarget.value.trim() });
              }
              setShowTextInput(false);
            } else if (e.key === 'Escape') {
              setShowTextInput(false);
            }
          }}
          style={{
            padding: '4px 8px',
            border: '1px solid rgb(229, 231, 235)',
            borderRadius: '4px',
            fontSize: '13px',
            width: '120px',
            outline: 'none',
          }}
        />
      )}

      {/* Text Background Toggle */}
      {text && (
        <button
          style={buttonStyle(textBackgroundEnabled === true && !hasMixedValues('textBackgroundEnabled'))}
          onClick={() => {
            const newEnabled = !textBackgroundEnabled;
            onUpdate({ 
              textBackgroundEnabled: newEnabled,
              textBackgroundColor: newEnabled ? (strokeColor || '#000000') : undefined,
            });
          }}
          title="Text Background"
          disabled={hasMixedValues('textBackgroundEnabled')}
        >
          <div
            style={{
              width: '16px',
              height: '16px',
              border: '1px solid rgb(229, 231, 235)',
              backgroundColor: textBackgroundEnabled ? (data.textBackgroundColor || strokeColor || '#000000') : 'transparent',
              opacity: hasMixedValues('textBackgroundEnabled') ? 0.5 : 1,
            }}
          />
        </button>
      )}

      {onClose && (
        <>
          <div style={dividerStyle} />
          <button
            style={buttonStyle(false)}
            onClick={onClose}
            title="Close"
          >
            <X size={16} />
          </button>
        </>
      )}
    </div>
  );
};

